import {trackObservabilityIssueAlertClick} from '../../services/analytics/observabilityEvents';
import {type HoveredIssueSource} from '../../state/issues/hooks';
import {type WorkspaceDrawerContextType} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {
  buildIssueAlerts,
  getIssuesWrapperStatus,
  type IssueAlertModel,
  type IssueStatus,
} from './issueMarkers';

/**
 * Issue state and actions from outside the plot. The functions and
 * `hoveredIssueSource` must be stable.
 */
export type IssueMarkerStateInputs = {
  activeIssueCount: number;
  annotationActive: boolean;
  entityName: string | undefined;
  handleIssueInteraction: (
    event: 'hover' | 'unhover',
    issueId: string | null
  ) => () => void;
  hoveredIssueSource: HoveredIssueSource;
  issueRampStatus: IssueStatus;
  /** Undefined outside a workspace, where there is no drawer. */
  openWorkspaceDrawer:
    | WorkspaceDrawerContextType['openWorkspaceDrawer']
    | undefined;
  projectName: string | undefined;
  setSelectedIssue: (issueId: string | null) => void;
  xAxisMax: number | null | undefined;
  xAxisMin: number | null | undefined;
};

/** Marker layout from the icon-positioning context provided inside Graph. */
export type IssueMarkerLayoutInputs = {
  hasAfterIssues: boolean;
  hasBeforeIssues: boolean;
  marginLeft: number;
  marginRight: number;
  sortedPositionedIssues: Array<[string, number]>;
};

export type IssueMarkerInputs = IssueMarkerStateInputs &
  IssueMarkerLayoutInputs;

type IssueMarkerViewProps = {
  /** Read by each issue marker to highlight itself while hovered. */
  hoveredIssueSource: HoveredIssueSource;
  /** Issue arrows and markers to draw above the plot, in order. */
  issueAlerts: IssueAlertModel[];
  /** Whether the layout reserves a row for issue markers. */
  issueStatus: IssueStatus;
  /** The pointer entered an issue marker (its ID) or left one (null). */
  onIssueHover: (issueId: string | null) => void;
  /** The user clicked an issue marker. */
  onIssueSelect: (issueId: string) => void;
};

/**
 * The issue-marker slice. Turns issue state and marker layout into the
 * markers to draw and the handlers for hovering and clicking them. A click
 * records analytics, opens the run issues drawer, and selects the issue.
 *
 * It holds no React state, so it is a plain function rather than a hook. Call
 * it while rendering a compiled component, which caches its result on its
 * inputs.
 */
export function getIssueMarkerViewProps({
  activeIssueCount,
  annotationActive,
  entityName,
  handleIssueInteraction,
  hasAfterIssues,
  hasBeforeIssues,
  hoveredIssueSource,
  issueRampStatus,
  marginLeft,
  marginRight,
  openWorkspaceDrawer,
  projectName,
  setSelectedIssue,
  sortedPositionedIssues,
  xAxisMax,
  xAxisMin,
}: IssueMarkerInputs): IssueMarkerViewProps {
  function onIssueHover(issueId: string | null) {
    handleIssueInteraction(issueId == null ? 'unhover' : 'hover', issueId)();
  }

  function onIssueSelect(issueId: string) {
    trackObservabilityIssueAlertClick({
      action: 'Click issue alert',
      entityName: entityName ?? '',
      projectName: projectName ?? '',
    });
    // Nothing opens the run issues drawer with a target section or panel, so
    // opening it again while it is open changes nothing and keeps it open.
    openWorkspaceDrawer?.('run-issues');
    setSelectedIssue(issueId);
  }

  return {
    hoveredIssueSource,
    issueAlerts: buildIssueAlerts({
      annotationActive,
      hasAfterIssues,
      hasBeforeIssues,
      marginLeft,
      marginRight,
      sortedPositionedIssues,
      xAxisMax,
      xAxisMin,
    }),
    issueStatus: getIssuesWrapperStatus({
      annotationActive,
      issueCount: activeIssueCount,
      issueRampStatus,
    }),
    onIssueHover,
    onIssueSelect,
  };
}
